import React, { useCallback, useEffect, useState } from 'react'

import { Button, Spinner } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import MTCaptcha from 'Utils/MTCaptcha'
import { NOOP } from 'Utils/stableDefaults'

import { CHECKOUT_CAPTCHA_ID } from '../../Constants/captcha'

function CaptchaStep({ locale = 'en', isSubmitting = false, onSubmit = NOOP }) {
  const [showIncompleteError, setShowIncompleteError] = useState(false)

  useEffect(() => {
    // When the SDK was preloaded by an earlier step (email/login), Utils/MTCaptcha
    // only renders on the rerender that follows the captchaLoad event, which has
    // already fired, so this fresh container stays blank unless rendered here,
    // after React commits the div.
    if (!window.mtcaptcha) return

    // Re-entry after a verified solve (e.g. "Not you?" guest reset): the shared
    // MTCaptcha state is still isVerified while getVerifiedToken would return an
    // already-consumed token. resetUI is a no-op here (the instance points at the
    // unmounted node), so drop the stale instance before rendering a fresh challenge.
    if (window.mtcaptcha.getStatus?.(CHECKOUT_CAPTCHA_ID)?.isVerified) {
      window.mtcaptcha.remove(CHECKOUT_CAPTCHA_ID)
    }

    window.mtcaptcha.renderUI?.(CHECKOUT_CAPTCHA_ID)
  }, [])

  const t = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale]
  )

  function handleContinue() {
    const token = window.mtcaptcha?.getVerifiedToken?.(CHECKOUT_CAPTCHA_ID)

    if (!token) {
      setShowIncompleteError(true)
      return
    }

    setShowIncompleteError(false)
    onSubmit(token)
  }

  return (
    <div className="captcha-step">
      <h1 className="captcha-step__title cgt-heading-300">
        {t('subscription_checkout.captcha_step.title')}
      </h1>
      <p className="captcha-step__subtitle cgt-body-base">
        {t('subscription_checkout.captcha_step.subtitle')}
      </p>

      <MTCaptcha captchaId={CHECKOUT_CAPTCHA_ID} forceVisible />

      {showIncompleteError && (
        <p className="captcha-step__error" role="alert">
          {t('subscription_checkout.captcha_step.incomplete')}
        </p>
      )}

      <Button
        expand
        size="large"
        actionType="button"
        disabled={isSubmitting}
        aria-label={t('subscription_checkout.captcha_step.continue_button')}
        aria-busy={isSubmitting}
        onClick={handleContinue}
      >
        {isSubmitting
          ? <Spinner size="xsmall" classNames="cgt-spinner--inline" />
          : t('subscription_checkout.captcha_step.continue_button')}
      </Button>
    </div>
  )
}

CaptchaStep.propTypes = {
  locale: string,
  isSubmitting: bool,
  onSubmit: func,
}

export default CaptchaStep
